import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { CirclePlus, Home, List as ListIcon, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";

const BottomBar = ({ role, loggedIn }) => {
  const { t } = useTranslation();
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const baselineHeightRef = useRef(
    typeof window !== "undefined"
      ? window.visualViewport?.height || window.innerHeight
      : 0
  );

  useEffect(() => {
    const viewport = window.visualViewport;

    const handleResize = () => {
      const currentHeight = viewport?.height || window.innerHeight;
      const baselineHeight = baselineHeightRef.current;

      if (currentHeight > baselineHeight - 40) {
        baselineHeightRef.current = currentHeight;
      }

      setKeyboardOpen(baselineHeightRef.current - currentHeight > 140);
    };

    const resetBaseline = () => {
      baselineHeightRef.current = viewport?.height || window.innerHeight;
      setKeyboardOpen(false);
    };

    viewport?.addEventListener("resize", handleResize);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", resetBaseline);

    return () => {
      viewport?.removeEventListener("resize", handleResize);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", resetBaseline);
    };
  }, []);

  if (!loggedIn || keyboardOpen) return null;

  const items = [
    {
      id: "home",
      label: t("navigation.home"),
      to: "/",
      icon: Home,
      end: true,
      visible: true,
    },
    {
      id: "entry",
      label: t("navigation.entry"),
      to: "/entries/milk-entry",
      icon: CirclePlus,
      visible: role !== "associated user",
    },
    {
      id: "records",
      label: t("navigation.records"),
      to: "/view-entries",
      icon: ListIcon,
      visible: true,
    },
    {
      id: "balance",
      label: t("navigation.balance"),
      to: "/balance",
      icon: Wallet,
      visible: true,
    },
  ].filter((item) => item.visible);

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-3 md:hidden"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <nav
        className="pointer-events-auto mx-auto grid min-h-[68px] max-w-lg items-center rounded-[24px] bg-white/95 px-2 shadow-[0_12px_36px_rgba(20,35,58,0.16)] ring-1 ring-black/[0.04] backdrop-blur-xl"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
        aria-label={t("navigation.label")}
      >
        {items.map(({ id, label, to, icon: Icon, end }) => (
          <NavLink
            key={id}
            to={to}
            end={end}
            className={({ isActive }) =>
              `group relative mx-0.5 flex min-h-[56px] flex-col items-center justify-center gap-1 rounded-2xl px-1 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
                isActive
                  ? "font-bold text-primary"
                  : "font-medium text-muted hover:bg-paper hover:text-primary"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={`h-[22px] w-[22px] ${isActive ? "stroke-[2.5]" : "stroke-2"}`}
                  aria-hidden="true"
                />
                <span className="max-w-full truncate text-[10.5px] leading-none">{label}</span>
                {isActive && (
                  <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-primary" aria-hidden="true" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default BottomBar;
